/**
 * Plan Approval card (PRD §17 + M1.5): select, rename, note, override holds (with impact), reorder the selection, approve —
 * or request changes, which re-runs the planner on the owner's feedback and proposes the next plan version.
 */
import { useEffect, useState } from "react";
import type { PlanRecord, SeriesStatus } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, ConfirmDialog, Field, Modal } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { clock } from "../../lib/format";
import * as P from "./planSelection";
import { AutoDecisions } from "../review/ReviewScreen";

type AutoItems = Parameters<typeof AutoDecisions>[0]["items"];

type Plan = PlanRecord["plan"];
type Ep = Plan["episodes"][number];

/** An episode's `sources` entries are source ids or titles; resolve them against the plan's source list. */
export function sourceLabel(plan: Plan, ref: string): { title: string; url?: string } {
  const s = plan.sources.find((x) => x.id === ref || x.title === ref);
  return s ? { title: s.title, url: s.url } : { title: ref };
}

export function PlanApprovalCard({ seriesId, record, seriesStatus }: { seriesId: string; record: PlanRecord; seriesStatus?: SeriesStatus }) {
  const api = useApi();
  const toast = useToast();
  const { plan } = record;
  const [draft, setDraft] = useState(() => P.initDraft(plan));
  const [editing, setEditing] = useState<number | null>(null);
  const [noting, setNoting] = useState<number[]>([]);
  const [overrideFor, setOverrideFor] = useState<Ep | null>(null);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keys = ["series", `messages/${seriesId}`, "dashboard", "decisions"];

  // Bridges the moment between "revise accepted" and the refetched series showing status=planning (SSE-driven after that).
  useEffect(() => { if (seriesStatus === "planning") setRequested(false); }, [seriesStatus]);

  const approve = useMutation((sel: Parameters<typeof api.approvePlan>[1]) => api.approvePlan(seriesId, sel), {
    invalidate: keys, onSuccess: () => { setConfirmApprove(false); toast.ok("Plan approved — the Studio is building."); }, onError: (e) => { setConfirmApprove(false); toast.error(e, "Couldn't approve"); },
  });
  const revise = useMutation((text: string) => api.revisePlan(seriesId, { plan_version: record.version, feedback: text }), {
    invalidate: keys,
    onSuccess: () => { setFeedback(null); setRequested(true); toast.ok(`Sent — the Studio is revising the plan (v${record.version + 1}).`); },
    onError: (e) => toast.error(e, "Couldn't send your changes"),
  });

  if (record.status !== "proposed") {
    return (
      <div className="plan-card plan-card-done">
        <Icon name="check" /> <span>Plan v{record.version} {record.status === "approved" ? "approved" : "superseded"} · {plan.series_title}</span>
      </div>
    );
  }

  const revising = requested || seriesStatus === "planning";
  const chosen = P.selectedEpisodes(plan, draft);
  const reordered = P.isReordered(plan, draft);
  const heldCount = plan.episodes.filter((e) => e.held).length;
  const attemptApprove = () => {
    const r = P.toSelection(plan, record.version, draft);
    if (!r.ok) { setError(r.error); return; }
    setError(null);
    setConfirmApprove(true);
  };

  return (
    <section className="plan-card" aria-label={`Series plan version ${record.version}`}>
      <header className="plan-head">
        <span className="plan-eyebrow">
          <span className="eyebrow">Series plan v{record.version} · {plan.episodes.length} episodes{heldCount ? ` · ${heldCount} held` : ""}</span>
          {record.version > 1 && <Badge tone="info">Revised</Badge>}
        </span>
        <h3>{plan.series_title}</h3>
        <p className="plan-spine">{plan.spine}</p>
        {Array.isArray((plan as unknown as { auto_decisions?: unknown }).auto_decisions) && ((plan as unknown as { auto_decisions: AutoItems }).auto_decisions).length > 0 && (
          <AutoDecisions items={(plan as unknown as { auto_decisions: AutoItems }).auto_decisions} />
        )}
        {record.version > 1 && plan.revision_notes && (
          <div className="plan-note-block tone-info" role="note" aria-label="What changed">
            <h4>What changed in v{record.version}</h4>
            <p className="pre">{plan.revision_notes}</p>
          </div>
        )}
        {plan.open_questions.length > 0 && (
          <div className="plan-note-block tone-attention" role="note" aria-label="Open questions">
            <h4>Open questions</h4>
            <ul>{plan.open_questions.map((q) => <li key={q}>{q}</li>)}</ul>
            <p className="muted small">Answer them in the conversation or in your change request.</p>
          </div>
        )}
        {plan.sources.length > 0 && (
          <details className="plan-more">
            <summary><Icon name="book" size={18} /> Sources ({plan.sources.length})</summary>
            <ol className="plan-sources">
              {plan.sources.map((s) => (
                <li key={s.id}>
                  {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{s.title} <Icon name="external" size={14} /></a> : <span>{s.title}</span>}
                  {s.note && <span className="muted small"> — {s.note}</span>}
                </li>
              ))}
            </ol>
          </details>
        )}
        <details className="plan-more">
          <summary>Source, drops and facts to verify</summary>
          <p>{plan.source_summary}</p>
          {plan.format_notes && <><h4>Format</h4><p>{plan.format_notes}</p></>}
          {plan.drop.length > 0 && <><h4>Dropped</h4><ul>{plan.drop.map((d) => <li key={d}>{d}</li>)}</ul></>}
          {plan.verify.length > 0 && <><h4>To verify before scripting</h4><ul>{plan.verify.map((d) => <li key={d}>{d}</li>)}</ul></>}
          {plan.brand.credit_line && <p className="muted">Credit: {plan.brand.credit_line}</p>}
        </details>
      </header>

      <div className="plan-toolbar">
        <span aria-live="polite">Selected: <b>{chosen.length}</b> of {plan.episodes.length} episodes</span>
        <span className="push" />
        <Button size="sm" variant="ghost" onClick={() => setDraft((d) => P.selectAll(d, plan))}>Select all</Button>
        <Button size="sm" variant="ghost" onClick={() => setDraft(P.selectNone)}>None</Button>
      </div>

      <ol className="plan-list">
        {plan.episodes.map((ep) => {
          const selectable = P.isSelectable(ep, draft);
          const selected = P.isSelected(ep, draft);
          const cid = `plan-${record.version}-ep-${ep.sequence}`;
          const overridden = ep.held && draft.overridden.includes(ep.sequence);
          return (
            <li key={ep.sequence} className={`plan-ep ${selected ? "is-selected" : ""} ${ep.held && !overridden ? "is-held" : ""}`}>
              <div className="plan-ep-row">
                <input id={cid} type="checkbox" className="check" checked={selected} disabled={!selectable} onChange={() => setDraft((d) => P.toggle(d, ep))} aria-describedby={ep.held ? `${cid}-held` : undefined} />
                <div className="plan-ep-main">
                  {editing === ep.sequence ? (
                    <input className="plan-rename" aria-label={`New title for episode ${ep.sequence}`} autoFocus defaultValue={P.effectiveTitle(ep, draft)}
                      onBlur={(e) => { setDraft((d) => P.rename(d, ep.sequence, e.target.value)); setEditing(null); }}
                      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditing(null); }} />
                  ) : (
                    <label htmlFor={cid} className="plan-ep-title">
                      <span className="muted">{ep.sequence}.</span> {P.effectiveTitle(ep, draft)}
                      {draft.renames[ep.sequence]?.trim() && draft.renames[ep.sequence]!.trim() !== ep.title && <span className="muted small"> (renamed)</span>}
                    </label>
                  )}
                  {plan.pilot === ep.sequence && <span className="chips-inline"><Badge tone="accent">Build first</Badge></span>}
                  <p className="plan-ep-idea">{ep.one_idea}</p>
                  <p className="plan-ep-hook">“{ep.hook}”</p>
                  <p className="plan-ep-meta muted small">
                    {[ep.source_span ? `${ep.source_span.start}–${ep.source_span.end}` : null, ep.est_duration_s ? `~${clock(ep.est_duration_s)}` : null, ep.visual_spine && ep.visual_spine !== "—" ? ep.visual_spine : null].filter(Boolean).join(" · ")}
                  </p>
                  {ep.sources && ep.sources.length > 0 && (
                    <ul className="chips-inline plan-ep-sources" aria-label={`Sources for episode ${ep.sequence}`}>
                      {ep.sources.map((ref) => {
                        const s = sourceLabel(plan, ref);
                        return <li key={ref} className="source-chip" title={s.title}><Icon name="book" size={14} /> <span>{s.title}</span></li>;
                      })}
                    </ul>
                  )}
                  {ep.sensitivity.length > 0 && <p className="chips-inline">{ep.sensitivity.map((s) => <Badge key={s} tone="attention">{s.replace(/_/g, " ")}</Badge>)}</p>}
                  {ep.held && (
                    <div id={`${cid}-held`} className={`held-note ${overridden ? "is-overridden" : ""}`}>
                      <Icon name="lock" size={18} />
                      <span><b>{overridden ? "Hold overridden." : "Held by the Studio."}</b> {ep.held_reason ?? "No reason given."}</span>
                      {overridden
                        ? <Button size="sm" variant="ghost" onClick={() => setDraft((d) => P.restoreHold(d, ep))}>Undo</Button>
                        : <Button size="sm" variant="secondary" onClick={() => setOverrideFor(ep)}>Override</Button>}
                    </div>
                  )}
                  {noting.includes(ep.sequence) && (
                    <textarea className="plan-note" rows={2} aria-label={`Note for episode ${ep.sequence}`} placeholder="A note for the Studio about this episode" value={draft.notes[ep.sequence] ?? ""} onChange={(e) => setDraft((d) => P.setNote(d, ep.sequence, e.target.value))} />
                  )}
                  <div className="plan-ep-actions">
                    <Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(ep.sequence)} aria-label={`Rename episode ${ep.sequence}`}>Rename</Button>
                    {!noting.includes(ep.sequence) && <Button size="sm" variant="ghost" icon="message" onClick={() => setNoting((n) => [...n, ep.sequence])} aria-label={`Add a note to episode ${ep.sequence}`}>Note</Button>}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {chosen.length > 1 && (
        <section className="plan-order" aria-label="Build order">
          <div className="plan-order-head">
            <h4>Build order</h4>
            {reordered && <Button size="sm" variant="ghost" onClick={() => setDraft((d) => P.resetOrder(plan, d))}>Reset to plan order</Button>}
          </div>
          <p className="muted small">The selected episodes are built and numbered in this order.</p>
          <ol className="plan-order-list">
            {chosen.map((ep, i) => {
              const title = P.effectiveTitle(ep, draft);
              return (
                <li key={ep.sequence}>
                  <span className="plan-order-n" aria-hidden>{i + 1}</span>
                  <span className="plan-order-title">{title}{ep.sequence !== i + 1 && <span className="muted small"> · plan #{ep.sequence}</span>}</span>
                  <button type="button" className="icon-btn" disabled={i === 0} aria-label={`Move “${title}” up`} onClick={() => setDraft((d) => P.move(plan, d, ep.sequence, -1))}><Icon name="arrowUp" size={20} /></button>
                  <button type="button" className="icon-btn" disabled={i === chosen.length - 1} aria-label={`Move “${title}” down`} onClick={() => setDraft((d) => P.move(plan, d, ep.sequence, 1))}><Icon name="arrowDown" size={20} /></button>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {error && <p className="field-error" role="alert">{error}</p>}
      {revising ? (
        <div className="plan-revising" role="status">
          <span className="spinner" aria-hidden />
          <span>
            <b>The Studio is revising the plan (v{record.version + 1})…</b>
            <span className="muted small"> It will appear here when it's ready. Nothing is built until you approve a plan.</span>
          </span>
        </div>
      ) : (
        <div className="plan-actions">
          <Button variant="secondary" icon="edit" onClick={() => setFeedback("")}>Request changes</Button>
          <Button variant="primary" size="lg" icon="check" disabled={!chosen.length} onClick={attemptApprove}>Approve {chosen.length} episode{chosen.length === 1 ? "" : "s"}</Button>
        </div>
      )}

      {feedback !== null && (
        <Modal variant="sheet" title={`Request changes to plan v${record.version}`} onClose={() => setFeedback(null)} footer={
          <>
            <Button variant="ghost" onClick={() => setFeedback(null)}>Cancel</Button>
            <Button variant="primary" icon="send" busy={revise.pending} disabled={!feedback.trim()} onClick={() => void revise.mutate(feedback.trim())}>Send to the Studio</Button>
          </>
        }>
          <p className="muted small">The Studio re-plans with your feedback and proposes v{record.version + 1}. Nothing is built until you approve.</p>
          <Field label="What should change?" hint="Anything: episodes to merge, drop or add, titles, length, angle.">
            {(id, d) => <textarea id={id} aria-describedby={d} rows={5} maxLength={8000} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="merge 3 and 4 · add an episode on … · shorter titles" />}
          </Field>
        </Modal>
      )}
      {overrideFor && (
        <ConfirmDialog title={`Override the hold on episode ${overrideFor.sequence}?`} confirmLabel="Override and select" tone="primary"
          impact={<>The Studio held “{overrideFor.title}” because: <b>{overrideFor.held_reason ?? "no reason given"}</b>. If you override, it will be built like any other episode and still passes every quality gate — but the Studio will not hold it again.</>}
          onCancel={() => setOverrideFor(null)} onConfirm={() => { setDraft((d) => P.overrideHold(d, overrideFor)); setOverrideFor(null); }} />
      )}
      {confirmApprove && (
        <ConfirmDialog title={`Approve ${chosen.length} episode${chosen.length === 1 ? "" : "s"}?`} confirmLabel="Approve and build" tone="primary" busy={approve.pending}
          impact={<>This commits the series. The Studio builds the selected episodes in this order, numbered 1–{chosen.length}, and end cards may promise the next one.</>}
          onCancel={() => setConfirmApprove(false)}
          onConfirm={() => { const r = P.toSelection(plan, record.version, draft); if (r.ok) void approve.mutate(r.value); }}>
          <ol className="confirm-list">{chosen.map((e) => <li key={e.sequence}>{P.effectiveTitle(e, draft)}{e.held ? " (hold overridden)" : ""}</li>)}</ol>
        </ConfirmDialog>
      )}
    </section>
  );
}
